import React from 'react';

const ACTION_FORM_ACCENTS = {
  booking: {
    badge: 'Booking',
    shell: 'from-cyan-500 via-teal-500 to-slate-900',
    chip: 'bg-cyan-100 text-cyan-700'
  },
  checkout: {
    badge: 'Checkout',
    shell: 'from-emerald-500 via-teal-500 to-slate-900',
    chip: 'bg-emerald-100 text-emerald-700'
  },
  inquiry: {
    badge: 'Inquiry',
    shell: 'from-amber-500 via-orange-500 to-slate-900',
    chip: 'bg-amber-100 text-amber-700'
  },
  custom: {
    badge: 'Action',
    shell: 'from-[#008080] via-teal-700 to-slate-900',
    chip: 'bg-[#008080]/10 text-[#0f6d6d]'
  }
};

export const SheetProfilePresetCard = ({
  row,
  profileHeader,
  profile,
  cardLabel,
  title,
  body,
  primaryColor,
  secondaryColor,
  titleColor,
  bodyColor,
  actionContent,
  compact = false,
  actionNotice,
  onActionClick,
  actionItems = [],
  isActionMenuOpen = false,
  onToggleActionMenu,
  onSelectActionItem,
  showOpenButton = false,
  onOpen
}) => {
  const themeStyle = {
    ...(primaryColor ? { '--sheet-primary-color': primaryColor } : {}),
    ...(secondaryColor ? { '--sheet-secondary-color': secondaryColor } : {})
  };
  return (
    <article
      className={`overflow-hidden rounded-[34px] border border-[#008080] bg-white shadow-sm ${compact ? '' : 'shadow-[0_28px_70px_-34px_rgba(15,23,42,0.28)]'}`}
      style={Object.keys(themeStyle).length ? themeStyle : undefined}
    >
      <div className="p-2">
        <div className="relative overflow-hidden rounded-[28px] border border-slate-300 bg-slate-200 shadow-[inset_0_2px_5px_rgba(0,0,0,0.3),0_0_10px_rgba(255,255,255,0.2)]">
          {profile?.imageUrl ? (
            <img
              src={profile.imageUrl}
              alt={profile.label || profileHeader?.label || 'Profile'}
              className={`${compact ? 'h-[220px]' : 'h-[320px]'} w-full object-cover`}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className={`${compact ? 'h-[220px]' : 'h-[320px]'} flex items-center justify-center bg-gradient-to-br from-slate-200 via-slate-100 to-white`}>
              <div className={`${compact ? 'h-20 w-20 text-3xl' : 'h-24 w-24 text-4xl'} flex items-center justify-center rounded-full border border-slate-100 bg-white/80 font-semibold uppercase text-slate-500 shadow-sm`}>
                {(profile?.label || cardLabel || '?').slice(0, 1)}
              </div>
            </div>
          )}
          <div className="absolute left-4 top-1 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-white/88 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600 backdrop-blur-sm">
              {profileHeader?.label || 'Profile'}
            </span>
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold ${
              row?.isNew
                ? 'bg-amber-100/95 text-amber-700'
                : row?.isDirty
                  ? 'bg-sky-100/95 text-sky-700'
                  : 'bg-emerald-100/95 text-emerald-700'
            }`}>
              {row?.isNew ? 'Draft' : row?.isDirty ? 'Edited' : 'Active'}
            </span>
          </div>
        </div>
      </div>

      <div className={`${compact ? 'px-4 pb-4 pt-1' : 'px-5 pb-5 pt-2'}`}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`${compact ? 'text-md' : 'text-lg'} min-w-0 truncate font-semibold tracking-tight text-slate-900`} style={titleColor ? { color: titleColor } : undefined}>
              {title || profile?.label || cardLabel}
            </h3>
            <span
              title="Verified"
              aria-label="Verified"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#008080] text-[11px] font-semibold uppercase text-white"
            >
              V
            </span>
          </div>
          {body ? (
            <p className="mt-2 line-clamp-2 text-sm leading-5 text-slate-500" style={bodyColor ? { color: bodyColor } : undefined}>
              {body}
            </p>
          ) : null}
        </div>

        <div className="mt-6 flex items-end justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-6 text-slate-700">
            {actionContent ? (
              <div className="min-w-0">
                {actionContent}
              </div>
            ) : (
              <span className="text-sm text-slate-400">
                {row?.isNew ? 'Draft profile' : row?.isDirty ? 'Edited profile' : 'Published profile'}
              </span>
            )}
          </div>

          <div className="relative flex shrink-0 items-center gap-2">
            {actionItems.length ? (
              <div className="relative" data-row-action-menu-root="true">
                <button
                  type="button"
                  onClick={onToggleActionMenu}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-[#008080]/35 hover:text-[#008080]"
                >
                  Smartchip
                </button>
                {isActionMenuOpen ? (
                  <div className="absolute bottom-full right-0 z-20 mb-2 min-w-[220px] rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                    <div className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Row actions
                    </div>
                    <div className="space-y-1">
                      {actionItems.map((actionItem) => (
                        <button
                          key={actionItem.id}
                          type="button"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onSelectActionItem?.(actionItem);
                          }}
                          className="flex w-full flex-col rounded-2xl px-3 py-2 text-left transition hover:bg-slate-50"
                        >
                          <span className="text-sm font-semibold text-slate-900">{actionItem.label}</span>
                          {actionItem.description ? (
                            <span className="mt-0.5 text-xs text-slate-500">{actionItem.description}</span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : actionNotice ? (
              <button
                type="button"
                onClick={onActionClick}
                className="inline-flex items-center rounded-full bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 transition hover:bg-amber-100"
              >
                {actionNotice}
              </button>
            ) : null}
            {showOpenButton && !actionContent ? (
              <button
                type="button"
                onClick={onOpen}
                className="inline-flex items-center rounded-full bg-slate-100 px-6 py-3 text-sm font-semibold text-slate-900 shadow-[0_14px_30px_-18px_rgba(15,23,42,0.35)] transition hover:bg-slate-200"
              >
                Open
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
};

export const SheetListCardPreset = ({
  row,
  imageUrl,
  imageLayout = 'inline',
  category,
  title,
  body,
  primaryColor,
  secondaryColor,
  categoryColor,
  titleColor,
  bodyColor,
  tags = [],
  actionLabel,
  actionNotice,
  onActionClick,
  actionItems = [],
  isActionMenuOpen = false,
  onToggleActionMenu,
  onSelectActionItem,
  cardLabel,
  showOpenButton = false,
  onOpen
}) => (
  <article
    className="h-full overflow-hidden rounded-[14px] border border-[#008080] bg-white shadow-[0_24px_60px_-36px_rgba(15,23,42,0.28)]"
    style={{
      ...(primaryColor ? { '--sheet-primary-color': primaryColor } : {}),
      ...(secondaryColor ? { '--sheet-secondary-color': secondaryColor } : {})
    }}
  >
    <div className="flex h-full min-h-[300px] flex-col p-2 sm:min-h-[320px] sm:p-2">
      <div className="flex items-start justify-between gap-3">
        <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          {row?.isNew ? 'Draft' : row?.isDirty ? 'Edited' : 'Saved'}
        </span>
        {showOpenButton ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpen?.(event);
              }}
              className="inline-flex items-center rounded-2xl bg-[#008080] px-4 py-1.5 text-sm font-semibold text-white transition hover:opacity-90"
              style={primaryColor ? { backgroundColor: primaryColor } : undefined}
            >
              Open
            </button>
          </div>
        ) : null}
      </div>

      {imageLayout === 'full-width' ? (
        <>
          <div className="mt-4 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400" style={categoryColor ? { color: categoryColor } : undefined}>
              {category || 'Item'}
            </p>
            <h3 className="mt-2 line-clamp-2 text-xl font-bold tracking-tight text-slate-900" style={titleColor ? { color: titleColor } : undefined}>
              {title || cardLabel}
            </h3>
          </div>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title || cardLabel || 'List card'}
              className="mt-4 h-40 w-full object-cover shadow-sm"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
        </>
      ) : (
        <div className="mt-4 flex items-start gap-4">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title || cardLabel || 'List card'}
              className="h-24 w-24 shrink-0 object-cover shadow-sm"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}

          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400" style={categoryColor ? { color: categoryColor } : undefined}>
              {category || 'Item'}
            </p>
            <h3 className="mt-2 line-clamp-2 text-xl font-bold tracking-tight text-slate-900" style={titleColor ? { color: titleColor } : undefined}>
              {title || cardLabel}
            </h3>
          </div>
        </div>
      )}

      <div className="flex-1 rounded-2xl bg-slate-50 px-4 py-3">
        <p className="line-clamp-4 text-md leading-6 text-slate-600" style={bodyColor ? { color: bodyColor } : undefined}>
          {body || 'No body text selected for this card.'}
        </p>
      </div>

      {actionNotice ? (
        <div className="mt-1">
          <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
            {actionNotice}
          </span>
        </div>
      ) : null}

      {tags.length ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={`${row?.id || 'row'}_${tag}`}
              className="inline-flex rounded-full border border-[#008080]/15 bg-[#008080]/10 px-3 py-1 text-xs font-semibold text-[#0f6d6d]"
            >
              {tag}
            </span>
          ))}
        </div>
      ) : null}

      {(actionItems.length || actionNotice) && (
        <div className="relative mt-3 flex justify-end" data-row-action-menu-root="true">
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (actionItems.length) {
                onToggleActionMenu?.(event);
                return;
              }
              onActionClick?.(event);
            }}
            className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-[#008080]/35 hover:text-[#008080]"
          >
            {actionItems.length ? 'Smartchip' : (actionNotice || actionLabel || 'Actions')}
          </button>
          {actionItems.length && isActionMenuOpen ? (
            <div className="absolute bottom-full right-0 z-20 mb-2 min-w-[220px] rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
              <div className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Row actions
              </div>
              <div className="space-y-1">
                {actionItems.map((actionItem) => (
                  <button
                    key={actionItem.id}
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onSelectActionItem?.(actionItem);
                    }}
                    className="flex w-full flex-col rounded-2xl px-3 py-2 text-left transition hover:bg-slate-50"
                  >
                    <span className="text-sm font-semibold text-slate-900">{actionItem.label}</span>
                    {actionItem.description ? (
                      <span className="mt-0.5 text-xs text-slate-500">{actionItem.description}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  </article>
);

export const SheetKanbanPresetCard = ({
  row,
  title,
  body,
  meta,
  primaryColor,
  secondaryColor,
  titleColor,
  bodyColor,
  metaColor,
  actionLabel,
  actionNotice,
  onActionClick,
  actionItems = [],
  isActionMenuOpen = false,
  onToggleActionMenu,
  onSelectActionItem,
  showOpenButton = false,
  onOpen
}) => (
  <article
    className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-3 shadow-md"
    style={{
      ...(primaryColor ? { '--sheet-primary-color': primaryColor } : {}),
      ...(secondaryColor ? { '--sheet-secondary-color': secondaryColor } : {})
    }}
  >
    <div className="flex items-start justify-between gap-3">
      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
        row?.isNew
          ? 'bg-[#008080]/10 text-[#008080]'
          : row?.isDirty
            ? 'bg-amber-100 text-amber-700'
            : 'bg-slate-100 text-slate-500'
      }`}>
        {row?.isNew ? 'Draft' : row?.isDirty ? 'Edited' : 'Saved'}
      </span>
      {showOpenButton ? (
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center rounded-xl bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-700"
        >
          Open
        </button>
      ) : null}
    </div>
    <h3 className="mt-3 line-clamp-2 text-base font-semibold tracking-tight text-slate-900" style={titleColor ? { color: titleColor } : undefined}>
      {title || 'Untitled task'}
    </h3>
    {body ? (
      <p className="mt-2 line-clamp-4 text-sm leading-6 text-slate-600" style={bodyColor ? { color: bodyColor } : undefined}>
        {body}
      </p>
    ) : null}
    {meta ? (
      <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500" style={metaColor ? { color: metaColor } : undefined}>
        {meta}
      </div>
    ) : null}
    {(actionItems.length || actionNotice) ? (
      <div className="relative mt-auto flex justify-end pt-3" data-row-action-menu-root="true">
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (actionItems.length) {
              onToggleActionMenu?.(event);
              return;
            }
            onActionClick?.(event);
          }}
          className="inline-flex items-center rounded-lg border bg-white px-3 py-1 text-xs font-semibold shadow-md transition border-[#008080]/35 text-[#008080]"
        >
          {actionItems.length ? 'smartchip' : (actionNotice || actionLabel || 'Actions')}
        </button>
        {actionItems.length && isActionMenuOpen ? (
          <div className="absolute bottom-full right-0 z-20 mb-2 min-w-[220px] rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
            <div className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Row actions
            </div>
            <div className="space-y-1">
              {actionItems.map((actionItem) => (
                <button
                  key={actionItem.id}
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onSelectActionItem?.(actionItem);
                  }}
                  className="flex w-full flex-col rounded-2xl px-3 py-2 text-left transition hover:bg-slate-50"
                >
                  <span className="text-sm font-semibold text-slate-900">{actionItem.label}</span>
                  {actionItem.description ? (
                    <span className="mt-0.5 text-xs text-slate-500">{actionItem.description}</span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    ) : null}
  </article>
);

export const SheetCalendarEventChip = ({
  title,
  meta,
  imageUrl,
  tone = 'default',
  primaryColor,
  secondaryColor,
  titleColor,
  metaColor,
  onOpen
}) => (
  <button
    type="button"
    onClick={onOpen}
    className={`w-full rounded-md border px-2.5 py-2 text-left transition ${
      tone === 'accent'
        ? 'border-[#008080]/20 bg-[#008080]/10 hover:bg-[#008080]/15'
        : 'border-slate-200 bg-white hover:bg-slate-50'
    }`}
    style={{
      ...(primaryColor ? { '--sheet-primary-color': primaryColor } : {}),
      ...(secondaryColor ? { '--sheet-secondary-color': secondaryColor } : {})
    }}
  >
    {imageUrl ? (
      <img
        src={imageUrl}
        alt={title || 'Calendar item'}
        className="mb-2 h-auto w-full rounded-lg"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    ) : null}
    <p className="truncate text-xs font-semibold text-slate-900" style={titleColor ? { color: titleColor } : undefined}>{title || 'Untitled event'}</p>
    {meta ? (
      <p className="mt-1 truncate text-[11px] text-slate-500" style={metaColor ? { color: metaColor } : undefined}>{meta}</p>
    ) : null}
  </button>
);

export const SheetCalendarPresetCard = ({
  title,
  meta,
  dateLabel,
  imageUrl,
  tone = 'default',
  primaryColor,
  secondaryColor,
  titleColor,
  metaColor,
  dateColor,
  onOpen
}) => (
  <button
    type="button"
    onClick={onOpen}
    className={`w-full overflow-hidden rounded-[10px] border text-left transition ${
      tone === 'accent'
        ? 'border-[#008080]/20 bg-[#008080]/5 hover:bg-[#008080]/10'
        : 'border-slate-200 bg-white hover:bg-slate-50'
    }`}
    style={{
      ...(primaryColor ? { '--sheet-primary-color': primaryColor } : {}),
      ...(secondaryColor ? { '--sheet-secondary-color': secondaryColor } : {})
    }}
  >
    {imageUrl ? (
      <img
        src={imageUrl}
        alt={title || 'Calendar item'}
        className="h-auto w-full"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    ) : null}
    <div className="flex-col gap-3 overflow-x-auto p-1">
      <section className={meta ? 'min-w-0' : 'col-span-2 min-w-0'}>
        <p className="break-words text-sm font-semibold text-slate-900" style={titleColor ? { color: titleColor } : undefined}>{title || 'Untitled event'}</p>
      </section>
      <section className="min-w-0">
        <p className="break-words text-sm text-slate-600" style={dateColor ? { color: dateColor } : undefined}>{dateLabel || 'No date'}</p>
      </section>
      {meta ? (
        <section className="col-span-2 min-w-0">
          <p className="break-words text-sm text-slate-600" style={metaColor ? { color: metaColor } : undefined}>{meta}</p>
        </section>
      ) : null}
    </div>
  </button>
);

export const SheetSidebarActionFormCard = ({
  type = 'custom',
  title,
  description,
  targetSheetTitle,
  fieldsCount = 0
}) => {
  const accent = ACTION_FORM_ACCENTS[type] || ACTION_FORM_ACCENTS.custom;

  return (
    <section className={`overflow-hidden rounded-[12px] border border-slate-200 bg-white shadow-sm`}>
      <div className={`bg-gradient-to-br ${accent.shell} px-5 py-6 text-white`}>
        <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80">
          {accent.badge}
        </span>
        <h3 className="mt-4 text-2xl font-bold tracking-tight">
          {title || accent.badge}
        </h3>
        <p className="mt-2 text-sm text-white/80">
          {description || 'Submit a dedicated action record without changing the current sheet sidebar flow.'}
        </p>
      </div>
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap gap-2">
          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${accent.chip}`}>
            {targetSheetTitle || 'Linked sheet'}
          </span>
          <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            {fieldsCount} field{fieldsCount === 1 ? '' : 's'}
          </span>
        </div>
      </div>
    </section>
  );
};
